import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Setting } from '../schemas/settings';

interface SettingsCacheDB extends DBSchema {
  settings: {
    key: string;
    value: {
      key: string;
      value: string;
      cachedAt: number;
      version: number;
    };
  };
  metadata: {
    key: string;
    value: {
      key: string;
      lastSync: number;
      cacheVersion: number;
    };
  };
}

class SettingsCacheService {
  private db: IDBPDatabase<SettingsCacheDB> | null = null;
  private readonly DB_NAME = 'erp-settings-cache';
  private readonly DB_VERSION = 1;
  private readonly TTL = 24 * 60 * 60 * 1000; // 24 hours

  async init(): Promise<void> {
    if (this.db) return;

    this.db = await openDB<SettingsCacheDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
      },
    });
  }

  async getSettings(): Promise<Setting[] | null> {
    if (!this.db) await this.init();
    if (!this.db) return null;

    try {
      // Check cache validity
      const isValid = await this.isCacheValid();
      if (!isValid) return null;

      const cachedItems = await this.db.getAll('settings');
      if (cachedItems.length === 0) return null;

      // Convert back to Setting objects
      return cachedItems.map(item => ({
        id: item.key, // Using key as ID for cache purposes if ID is missing, but usually Setting has ID
        key: item.key,
        value: item.value,
        created_at: new Date(item.cachedAt).toISOString(), // Mock dates
        updated_at: new Date(item.cachedAt).toISOString(),
      }));
    } catch (error) {
      console.error('Error reading settings from cache:', error);
      return null;
    }
  }

  async setSettings(settings: Setting[]): Promise<void> {
    if (!this.db) await this.init();
    if (!this.db) return;

    try {
      const tx = this.db.transaction(['settings', 'metadata'], 'readwrite');
      const settingsStore = tx.objectStore('settings');
      const metadataStore = tx.objectStore('metadata');

      // Clear existing settings
      await settingsStore.clear();

      // Add new settings
      const now = Date.now();
      for (const setting of settings) {
        await settingsStore.put({
          key: setting.key,
          value: setting.value,
          cachedAt: now,
          version: 1,
        });
      }

      // Update metadata
      await metadataStore.put({
        key: 'sync_info',
        lastSync: now,
        cacheVersion: 1,
      });

      await tx.done;
    } catch (error) {
      console.error('Error writing settings to cache:', error);
    }
  }

  async getSetting(key: string): Promise<string | null> {
    if (!this.db) await this.init();
    if (!this.db) return null;

    try {
      const item = await this.db.get('settings', key);
      if (!item) return null;

      // Check TTL for individual item (though we usually check global TTL)
      if (Date.now() - item.cachedAt > this.TTL) {
        return null;
      }

      return item.value;
    } catch (error) {
      console.error(`Error reading setting ${key} from cache:`, error);
      return null;
    }
  }

  async invalidateCache(): Promise<void> {
    if (!this.db) await this.init();
    if (!this.db) return;

    try {
      await this.db.clear('settings');
      await this.db.clear('metadata');
    } catch (error) {
      console.error('Error invalidating cache:', error);
    }
  }

  async isCacheValid(): Promise<boolean> {
    if (!this.db) await this.init();
    if (!this.db) return false;

    try {
      const metadata = await this.db.get('metadata', 'sync_info');
      if (!metadata) return false;

      const now = Date.now();
      return (now - metadata.lastSync) < this.TTL;
    } catch (error) {
      console.error('Error checking cache validity:', error);
      return false;
    }
  }
}

export const settingsCache = new SettingsCacheService();

